import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Copy, Merge, RefreshCw, X } from "lucide-react";
import { Avatar, Button, EmptyState, IconButton, Pill, Spinner, Switch, toast } from "@/ui";
import { useEngineMutation, useEngineQuery } from "@/engine/hooks";
import { useOpenContact } from "@/app/navigation";
import { Page } from "@/app/layout/Page";
import s from "../shared.module.css";

export function DuplicatesPage() {
  const navigate = useNavigate();
  const open = useOpenContact();
  const dups = useEngineQuery("listPotentialDuplicates", undefined);
  const settings = useEngineQuery("getWorkspaceSettings", undefined);
  const scan = useEngineMutation("scanDuplicates", { invalidate: ["listPotentialDuplicates"] });
  const merge = useEngineMutation("mergeContacts", { invalidate: ["listPotentialDuplicates", "queryContacts", "getContact"] });
  const unmerge = useEngineMutation("unmergeContact", { invalidate: ["listPotentialDuplicates", "queryContacts", "getContact", "listMerges", "searchArchivedContacts"] });
  const dismiss = useEngineMutation("dismissPotentialDuplicate", { invalidate: ["listPotentialDuplicates"] });
  const removeOne = useEngineMutation("removePotentialDuplicateContact", { invalidate: ["listPotentialDuplicates"] });
  const setAutomerge = useEngineMutation("setDuplicateAutomergeEnabled", { invalidate: ["getWorkspaceSettings"] });
  const [targets, setTargets] = useState<Record<string, string>>({});

  return (
    <Page
      before={<IconButton label="Back" onClick={() => navigate("/people")}><ArrowLeft size={16} /></IconButton>}
      title="Duplicates"
      actions={
        <>
          <span className="u-row u-small" style={{ gap: 8 }}>
            Merge exact matches automatically
            <Switch on={settings.data?.automergeEnabled ?? true} onChange={(v) => setAutomerge.mutate({ enabled: v })} label="Automerge" />
          </span>
          <Button size="sm" variant="outline" icon={<RefreshCw size={13} />} loading={scan.isPending} onClick={() => scan.mutate(undefined, { onSuccess: (r) => toast(`${r.found} possible duplicates`) })}>Scan again</Button>
        </>
      }
    >
      <p className="u-muted" style={{ marginTop: 0 }}>Pairs that look like the same person: identical names, close spellings, or a shared email or phone. Merging keeps everything from both records and can be undone.</p>
      {dups.isLoading && <Spinner />}
      {dups.isSuccess && dups.data.length === 0 && <EmptyState icon={<Copy size={26} className="u-muted" />} title="No duplicates to review">Hub checks for duplicates after every import and whenever a new identifier is added.</EmptyState>}
      <div className="u-col" style={{ gap: 10 }}>
        {(dups.data ?? []).map((d) => {
          const target = targets[d.id] ?? d.contacts[0]?.contactId;
          return (
            <div key={d.id} style={{ border: "1px solid var(--border)", borderRadius: 10, padding: 12, background: "var(--bg-elevated)" }}>
              <div className="u-row" style={{ marginBottom: 8 }}>
                <Pill tone={d.score >= 0.9 ? "success" : "warning"}>{Math.round(d.score * 100)}% match</Pill>
                <span className="u-small u-muted">{d.source === "exact_name" ? "Same name" : d.source === "fuzzy_name" ? "Similar name" : d.source === "shared_lookup" ? "Shared identifier" : "Same company and name"}</span>
                {d.canAutomerge && <Pill tone="accent">Safe to merge</Pill>}
                <span className="u-grow" />
                <Button size="sm" variant="ghost" onClick={() => dismiss.mutate({ duplicateId: d.id })}>Not duplicates</Button>
              </div>
              <div className={s.cardGrid} style={{ gridTemplateColumns: `repeat(${Math.max(2, d.contacts.length)}, 1fr)` }}>
                {d.contacts.map((c) => (
                  <label key={c.contactId} style={{ border: `1px solid ${target === c.contactId ? "var(--accent)" : "var(--border)"}`, borderRadius: 8, padding: 10, cursor: "pointer", position: "relative" }}>
                    <div className="u-row" style={{ gap: 10 }}>
                      <input type="radio" name={`t-${d.id}`} checked={target === c.contactId} onChange={() => setTargets((t) => ({ ...t, [d.id]: c.contactId }))} />
                      <Avatar name={c.displayName} src={c.avatarUrl} size={36} tier={c.bucket} seed={c.contactId} />
                      <div className="u-grow" style={{ minWidth: 0 }}>
                        <button type="button" onClick={(e) => { e.preventDefault(); open(c.contactId); }} style={{ border: "none", background: "transparent", padding: 0, fontWeight: 600, cursor: "pointer", color: "inherit" }}>{c.displayName}</button>
                        <div className="u-small u-muted u-truncate">{c.headline ?? "No headline"}</div>
                        <div className="u-small u-muted u-truncate">{c.primaryEmail ?? "No email"}{c.primaryPhone ? `, ${c.primaryPhone}` : ""}</div>
                      </div>
                      {d.contacts.length > 2 && <IconButton label="Remove from this pair" onClick={(e) => { e.preventDefault(); removeOne.mutate({ duplicateId: d.id, contactId: c.contactId }); }}><X size={13} /></IconButton>}
                    </div>
                    <div className="u-small u-muted" style={{ marginTop: 6 }}>{target === c.contactId ? "Keep this record" : "Merge into the other"}</div>
                  </label>
                ))}
              </div>
              <div className="u-row" style={{ justifyContent: "flex-end", marginTop: 8 }}>
                <Button variant="primary" size="sm" icon={<Merge size={13} />} loading={merge.isPending} disabled={!target || d.contacts.length < 2} onClick={() => merge.mutate({ targetContactId: target!, sourceContactIds: d.contacts.map((c) => c.contactId).filter((id) => id !== target) }, { onSuccess: (r) => toast("Merged", "success", { label: "Undo", onClick: () => Promise.all(r.mergeIds.map((mergeId) => unmerge.mutateAsync({ mergeId }))).then(() => toast("Merge undone", "success")).catch(() => undefined) }) })}>
                  Merge
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </Page>
  );
}
